import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { Bold, Italic, Link as LinkIcon, List, ListOrdered } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  editorClassName?: string;
  minHeight?: number;
  ariaLabel?: string;
}

// Constrained rich-text editor used by the accountant email composer.
// We only surface bold / italic / lists / links — Tiptap's StarterKit brings
// more than that, but we don't expose it in the toolbar. Anything else stays
// plain text so clients receive predictable, render-safe HTML.
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  editorClassName,
  minHeight = 120,
  ariaLabel = "Message body",
}: RichTextEditorProps) {
  const [isFocused, setIsFocused] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        code: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm max-w-none focus:outline-none min-h-[var(--rt-min)] px-3 py-2 text-sm",
          editorClassName,
        ),
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": ariaLabel,
      },
    },
  });

  // Keep editor content in sync when the parent resets the value externally
  // (e.g. template swap in the reminder dialog).
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) editor.commands.setContent(value || "");
  }, [value, editor]);

  if (!editor) return null;

  // Hide placeholder the moment the user engages (focus) OR adds text.
  // Relying on `getText().trim()` alone leaves the hint visible when the
  // first keystroke is Enter — the doc becomes `<p></p><p></p>` which still
  // has empty plain text. Tracking focus covers that case without needing
  // to inspect the ProseMirror doc shape.
  const showPlaceholder =
    !!placeholder && !isFocused && editor.getText().trim().length === 0;

  return (
    <div
      className={cn(
        "rounded-md border border-input bg-transparent",
        disabled && "opacity-60",
        className,
      )}
      style={{ ["--rt-min" as string]: `${minHeight}px` }}
    >
      <Toolbar editor={editor} disabled={disabled} />
      <div className="relative border-t border-input">
        {showPlaceholder && (
          <p className="pointer-events-none absolute mt-2 ml-3 text-sm text-muted-foreground">
            {placeholder}
          </p>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function Toolbar({ editor, disabled }: { editor: Editor; disabled?: boolean }) {
  const btn = (active: boolean) =>
    cn(
      "size-7 rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground",
      active && "bg-muted text-foreground",
    );

  const addLink = () => {
    const prev = editor.getAttributes("link").href ?? "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="flex items-center gap-0.5 px-1 py-1">
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        disabled={disabled}
        aria-label="Bold"
        title="Bold"
        className={btn(editor.isActive("bold"))}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="size-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        disabled={disabled}
        aria-label="Italic"
        title="Italic"
        className={btn(editor.isActive("italic"))}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="size-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        disabled={disabled}
        aria-label="Bullet list"
        title="Bullet list"
        className={btn(editor.isActive("bulletList"))}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="size-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        disabled={disabled}
        aria-label="Numbered list"
        title="Numbered list"
        className={btn(editor.isActive("orderedList"))}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="size-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        disabled={disabled}
        aria-label="Link"
        title="Insert link"
        className={btn(editor.isActive("link"))}
        onClick={addLink}
      >
        <LinkIcon className="size-3.5" />
      </Button>
    </div>
  );
}
